import React, { useState } from 'react'
import { motion } from 'framer-motion'
import Card from '../../../../../components/ui/Card'
import { MenuItem, updateMenuItem } from '../../../../../utils/supabaseClient'
import { useTranslation } from 'react-i18next'
import { useAppSelector, useAppDispatch } from '../../../../../store/hooks'
import { selectCurrentBranch } from '../../../../../store/selectors'
import { getItemDescription, getItemName, getItemPrice } from '../../../../../utils/menuItemHelper'
import Badge from '../../../../../components/ui/Badge'
import { useNavigate } from 'react-router-dom'
import { FiEdit2, FiEye, FiEyeOff, FiTrash2 } from 'react-icons/fi'
import { useBranch } from '../../../../../context/BranchContext'
import { toggleMenuItemPublishStatus } from '../../../../../store/slices/menuItemSlice'

interface MenuItemGridCardProps {
	item: MenuItem,
	handleEditClick?: (menuItem: MenuItem) => void,
	handleDeleteClick?: (menuItem: MenuItem) => void,
	onPublishToggle?: (updatedItem: MenuItem) => void
}

const MenuItemGridCard: React.FC<MenuItemGridCardProps> = ({
	item,
	handleEditClick = () => { },
	handleDeleteClick = () => { },
	onPublishToggle = () => { }
}) => {
	const { t, i18n } = useTranslation()
	const navigate = useNavigate()
	const dispatch = useAppDispatch()
	const branch = useAppSelector(selectCurrentBranch)
	const { selectedCurrency } = useBranch()
	const [isTogglingPublish, setIsTogglingPublish] = useState(false)

	const currencyCode = selectedCurrency?.iso || 'USD'
	const rate = selectedCurrency?.is_base ? 1 : (selectedCurrency?.exchange_rate ?? 1)
	const baseAmount = getItemPrice(item)
	const displayAmount = baseAmount * rate
	const originalBase = item.price ?? 0
	const originalDisplay = originalBase * rate

	const itemVariants = {
		hidden: { y: 20, opacity: 0 },
		visible: { y: 0, opacity: 1 }
	};

	const handlePublishToggle = async (e: React.MouseEvent) => {
		e.preventDefault()
		e.stopPropagation()
		if (isTogglingPublish) {
			return
		}

		const previousStatus = item.is_published
		const nextStatus = !previousStatus

		setIsTogglingPublish(true)
		dispatch(toggleMenuItemPublishStatus(item.id))
		onPublishToggle({ ...item, is_published: nextStatus })

		try {
			await updateMenuItem({
				id: item.id,
				is_published: nextStatus
			})
		} catch (error) {
			console.error('Error toggling publish status:', error)
			dispatch(toggleMenuItemPublishStatus(item.id))
			onPublishToggle({ ...item, is_published: previousStatus })
		} finally {
			setIsTogglingPublish(false)
		}
	}

	const handleCardClick = () => {
		navigate(`/dashboard/${branch?.id}/menu/${item.id}`)
	}

	return (
		<motion.div variants={itemVariants} className="h-full">
			<div
				className="h-full cursor-pointer"
				onClick={handleCardClick}
			>
				<Card className="h-full hover:shadow-md transition-shadow flex flex-col border border-gray-200 dark:border-gray-700">
					<div className="aspect-w-16 aspect-h-9 mb-4 bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden flex-shrink-0">
						{item.image_url ? (
							<img
								src={item.image_url}
								alt={item.name}
								className="w-full h-32 object-cover"
							/>
						) : (
							branch?.default_item_image ? (
								<img
									src={branch?.default_item_image}
									alt={item.name}
									className="w-full h-32 object-cover"
								/>
							) : (
								<div className="flex items-center justify-center h-32">
									<span className="text-gray-500 dark:text-gray-400 text-sm">
										{t('menuItems.noImage')}
									</span>
								</div>
							)
						)}

						
					</div>
<div className="flex flex-col flex-1">
						<div className="flex flex-col gap-2 items-start justify-between mb-2">
							<h3 className="text-lg font-medium text-gray-900 dark:text-white truncate w-full" title={getItemName(item, i18n.language)}>
								{getItemName(item, i18n.language)}
							</h3>
							<div className="flex flex-wrap gap-1 justify-end flex-shrink-0">
								{!item.is_published && (
									<Badge variant="gray" className="text-xs">
										{t('menuItems.draft')}
									</Badge>
								)}
								{!item.is_available && (
									<Badge variant="error" className="text-xs">
										{t('menuItems.unavailable')}
									</Badge>
								)}
								{item.is_featured && (
									<Badge variant="primary" className="text-xs">
										{t('menuItems.featured')}
									</Badge>
								)}
							</div>
						</div>

						<p className="text-sm text-gray-500 dark:text-gray-400  mb-3 h-10 overflow-hidden">
							{getItemDescription(item, i18n.language) || <span className="italic">{t('menuItems.noDescription')}</span>}
						</p>

						{/* Footer: fixed height to ensure prices and actions align across cards */}
						<div className="mt-auto border-t border-gray-200 dark:border-gray-700">
							<div className="py-3 h-22 flex items-center justify-between">
								<div className="flex flex-col justify-center font-medium text-gray-900 dark:text-white">
									{new Intl.NumberFormat(i18n.language, {
										style: 'currency',
										currency: currencyCode,
									}).format(displayAmount)}

									{!!item.discounted_price && (
										<span className="text-sm text-red-600 line-through">
											{new Intl.NumberFormat(i18n.language, {
												style: 'currency',
												currency: currencyCode,
											}).format(originalDisplay)}
										</span>
									)}
										<div className="flex gap-2 items-center pt-3">
									<button
										onClick={handlePublishToggle}
										disabled={isTogglingPublish}
										className="text-gray-400 hover:text-blue-500 dark:text-gray-500 dark:hover:text-blue-400 disabled:opacity-50 cursor-pointer transition-colors"
										title={item.is_published ? t('common.unpublish') : t('common.publish')}
									>
										{isTogglingPublish ? (
											<div className="animate-spin">
												<FiEyeOff className="h-5 w-5" />
											</div>
										) : item.is_published ? (
											<FiEye className="h-5 w-5" />
										) : (
											<FiEyeOff className="h-5 w-5" />
										)}
										<span className="sr-only">{item.is_published ? t('common.unpublish') : t('common.publish')}</span>
									</button>
									<button
										onClick={(e) => {
											e.preventDefault()
											e.stopPropagation()
											handleEditClick(item)
										}}
										className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400 cursor-pointer"
									>
										<FiEdit2 className="h-5 w-5" />
										<span className="sr-only">{t('common.edit')}</span>
									</button>
									<button
										onClick={(e) => {
											e.preventDefault()
											e.stopPropagation()
											handleDeleteClick(item)
										}}
										className="text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 cursor-pointer"
									>
										<FiTrash2 className="h-5 w-5" />
										<span className="sr-only">{t('common.delete')}</span>
									</button>
								</div>
								</div>
							
							</div>
						</div>

							
						</div>
					</Card>
				</div>
			</motion.div>
	)
}

export default MenuItemGridCard